import type { ReactNode } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import type { Deck, Slide } from '@/framework/types';
import {
  Header,
  TitleSlide,
  FigureSlide,
} from '@/framework/layouts';
import type { BoothDeckConfig } from './types';
import './styles.css';

// Cost-driver diagrams (round 4: replaced traps / ursa diagrams)
import costDriversKafka from './images/cost-drivers-kafka.png';
import costDriversUrsa from './images/cost-drivers-ursa.png';

// Architecture + proof art
import ufkArchitecture from './images/ufk-architecture.png';
import streamTableDuality from './images/stream-table-duality.png';
import lakestreamArchitecture from './images/lakestream-architecture.png';
import vldb2025 from './images/vldb-2025.webp';
import ursaBenchmark from './images/ursa-benchmark.png';

// Platform / ecosystem hero art (round 4: replaced platform-* + uniconn/unilink)
import inPracticePulsar from './images/in-practice-pulsar.webp';
import inPracticeKafka from './images/in-practice-kafka.webp';
import ecoConnect from './images/eco-connect-visual.svg';
import ecoUniversalLinking from './images/eco-universal-linking-visual.svg';
import ecoFlink from './images/eco-flink-visual.svg';
import ecoConnectBg from './images/eco-connect.webp';
import ecoUniversalLinkingBg from './images/eco-universal-linking.webp';
import ecoFlinkBg from './images/eco-flink.webp';

// Pulsar figure (slide 12) still uses the platform-pulsar diagram
import platformPulsar from './images/platform-pulsar.webp';

// Industry images
import industryFinserv from './images/industries/financial-services.webp';
import industryMartech from './images/industries/martech.webp';
import industryManufacturing from './images/industries/manufacturing.webp';
import industryGaming from './images/industries/gaming.webp';

// Customer logos
import logoInnerspace from './images/logos/innerspace.svg';
import logoSafariai from './images/logos/safariai.svg';
import logoUnify from './images/logos/unify.svg';
import logoIntercom from './images/logos/intercom.svg';
import logoIterable from './images/logos/iterable.svg';
import logoCisco from './images/logos/cisco.svg';
import logoToyota from './images/logos/toyota.svg';
import logoAppliedMaterials from './images/logos/applied-materials.svg';
import logoNarvar from './images/logos/narvar.svg';
import logoDatabricks from './images/logos/databricks.svg';
import logoNimble from './images/logos/nimble.svg';
import logoNetdata from './images/logos/netdata.svg';
import logoIntuit from './images/logos/intuit.svg';

const VLDB_PAPER_URL =
  'https://vldb.org/pvldb/volumes/18/paper/Ursa%3A%20A%20Lakehouse-Native%20Data%20Streaming%20Engine%20for%20Kafka';

/** Default deck title shared by every booth deck (config.title can override). */
export const DEFAULT_BOOTH_TITLE = 'Escape the Traps of Traditional Kafka.';

/* ══════════════════════════════════════════════════════════════════════
   Slides 1-3 — framework-driven hero + cost-driver diagrams
   (the title slide is built per-event inside buildBoothSlides)
   ══════════════════════════════════════════════════════════════════════ */

const TrapsSlide = () => (
  <FigureSlide
    eyebrow="Traditional Kafka"
    title={
      <>
        Escape the <span className="csl-trap-em">Cost, Scale, &amp; Elasticity</span> Traps.
      </>
    }
    sub="Traditional Kafka taxes you on every hop — replication, compute, connectors, duplicate copies."
    image={costDriversKafka}
    alt="Cost drivers of traditional Apache Kafka"
  />
);

const UrsaSlide = () => (
  <FigureSlide
    eyebrow="↓ With StreamNative Ursa ↓"
    title={
      <>
        Leader<span className="csl-solve-suffix">less</span>.{' '}
        Disk<span className="csl-solve-suffix">less</span>.{' '}
        State<span className="csl-solve-suffix">less</span>.{' '}
        Cost <span className="csl-solve-suffix">Less</span>.
      </>
    }
    sub="Ursa eliminates each cost driver. Same Kafka API, same clients, same tooling — on a lakehouse-native, leaderless engine."
    image={costDriversUrsa}
    alt="Cost drivers of StreamNative Ursa"
  />
);

/* ══════════════════════════════════════════════════════════════════════
   Slide 4 — UFK launch · Figure 1 architecture diagram
   ══════════════════════════════════════════════════════════════════════ */

const LaunchSlide = () => (
  <FigureSlide
    eyebrow="April 2026 Launch"
    title={<>Introducing <em>Ursa For Kafka.</em></>}
    sub="A native Apache Kafka 4.2+ fork that extends Kafka's local disk storage with Lakestream's lakehouse-native storage foundation."
    image={ufkArchitecture}
    alt="Ursa For Kafka Architecture — Figure 1 from the UFK announcement blog"
  />
);

/* ══════════════════════════════════════════════════════════════════════
   Slide 5 — Stream-Table Duality (new image3)
   ══════════════════════════════════════════════════════════════════════ */

const DualitySlide = () => (
  <FigureSlide
    eyebrow="Stream ↔ Table · Inside UFK"
    title={<>Every Kafka topic is <em>simultaneously</em> an Iceberg/Delta table.</>}
    sub="One copy of data. Two interfaces. Streaming ships Kafka. Batch queries Iceberg."
    image={streamTableDuality}
    alt="Stream-Table Duality diagram from the Ursa For Kafka blog"
  />
);

/* ══════════════════════════════════════════════════════════════════════
   Slide 6 — Lakestream Architecture (re-framed)
   ══════════════════════════════════════════════════════════════════════ */

const LakestreamArchitectureSlide = () => (
  <FigureSlide
    eyebrow="Inside the Lakestream Architecture · Deep Dive"
    title={<>The <em>Lakestream</em> Architecture.</>}
    sub="One unified architecture. Three independent layers — Protocol, Metadata, Data — all sharing the same governed data, each scaling on its own."
    image={lakestreamArchitecture}
    alt="Lakestream Architecture — three layers (Protocol, Metadata, Data) over object storage"
  />
);

/* ══════════════════════════════════════════════════════════════════════
   Slide 7 — VLDB award (3 benefit bullets, not implementation)
   ══════════════════════════════════════════════════════════════════════ */

const AwardSlide = () => (
  <div className="slide-body">
    <Header
      eyebrowTone="teal"
      eyebrow="VLDB 2025"
      title={<><em>Ursa</em> won the best industry paper of <em>VLDB 2025</em>.</>}
    />
    <div className="sn-work sn-work--row">
      <div className="csl-vldb-left">
        <p className="sn-sub">
          Ursa — the first lakehouse-native, leaderless streaming engine recognized at VLDB.
          Validated alongside the strongest industry papers of the year.
        </p>
        <ul className="csl-feature-list">
          <li><strong>Validated at scale</strong> — 5 GB/s sustained throughput, RF = 3 across 3 AZs.</li>
          <li><strong>Up to 95% lower TCO</strong> — cross-AZ replication eliminated; storage on object stores.</li>
          <li><strong>Zero client changes</strong> — native Apache Kafka API; existing producers and consumers work unchanged.</li>
        </ul>
        <p className="csl-cta-url">vldb.org/pvldb/volumes/18/paper/Ursa</p>
      </div>
      <div className="csl-cta-right">
        <img className="csl-vldb-logo" src={vldb2025} alt="VLDB 2025" />
        <div className="csl-qr-transparent">
          <QRCodeSVG value={VLDB_PAPER_URL} size={240} level="M" bgColor="transparent" fgColor="#202939" marginSize={0} />
        </div>
        <span className="sn-mono">Scan to read the paper</span>
      </div>
    </div>
  </div>
);

/* ══════════════════════════════════════════════════════════════════════
   Slide 8 — Benchmark (title now restates the headline)
   ══════════════════════════════════════════════════════════════════════ */

const BenchmarkSlide = () => (
  <div className="slide-body">
    <Header
      eyebrow="5 GB/s · ~5% the Cost of Kafka"
      title={<>5 GB/s at <em>~5%</em> the cost of Kafka.</>}
    />
    <div className="sn-work sn-work--row">
      <div className="csl-bench-left">
        <ul className="csl-bench-stats">
          <li>
            <strong>$54 / hr</strong>
            <span>Ursa @ 5 GB/s sustained throughput</span>
          </li>
          <li>
            <strong>$303 / hr</strong>
            <span>AWS MSK — same workload</span>
          </li>
          <li>
            <strong>$988 / hr</strong>
            <span>Redpanda — same workload</span>
          </li>
        </ul>
        <p className="sn-sub">
          RF=3 across three availability zones. Cross-AZ replication eliminated by Ursa's
          leaderless, diskless architecture.
        </p>
      </div>
      <div className="csl-bench-right">
        <img src={ursaBenchmark} alt="Cost comparison chart — Ursa vs MSK vs Redpanda at 5 GB/s" />
      </div>
    </div>
  </div>
);

/* ══════════════════════════════════════════════════════════════════════
   Slide 9 — Databricks quote (centered, with logo)
   ══════════════════════════════════════════════════════════════════════ */

const DatabricksQuoteSlide = () => (
  <div className="slide-body">
    <Header eyebrowTone="teal" eyebrow="Databricks Endorses the Approach" />
    <div className="sn-work csl-quote-centered">
      <img className="csl-quote-logo" src={logoDatabricks} alt="Databricks" />
      <blockquote className="csl-quote-hero">
        “This benchmark clearly demonstrates the benefits of a Lakehouse-first streaming approach.
        Our testing at Databricks <em>validates these results.</em>”
      </blockquote>
      <div className="csl-quote-attribution">
        — <strong>Karthik Ramasamy</strong> · Head of Streaming, Databricks
      </div>
    </div>
  </div>
);

/* ══════════════════════════════════════════════════════════════════════
   Slide 10 — Nimble Robotics case study (centered quote + 3 callouts)
   ══════════════════════════════════════════════════════════════════════ */

type CaseStat = { head: string; body: string };
const NIMBLE_STATS: ReadonlyArray<CaseStat> = [
  {
    head: '4–5×',
    body: 'lower streaming cost at target volume vs Confluent Cloud.',
  },
  {
    head: 'Cost is no longer a topic',
    body: 'Architecture decisions made on technical merit, not on next quarter’s bill.',
  },
  {
    head: 'AI training unlocked',
    body: 'Fleet-wide sensor data + video frames feed ML pipelines.',
  },
];

const CaseSlide = () => (
  <div className="slide-body">
    <Header eyebrow="Customer · Nimble Robotics" />
    <div className="sn-work csl-case-centered">
      <div className="csl-nimble-brand">
        <img className="csl-nimble-logo" src={logoNimble} alt="Nimble Robotics" />
        <span className="csl-nimble-tag">nimble.ai · Robotics-driven 3PL</span>
      </div>
      <blockquote className="csl-quote-hero">
        “Before, we were really conservative about producing more data because we were worried
        about the cost. After migrating, it’s more like — <em>okay, it’s not a topic anymore.</em>”
      </blockquote>
      <div className="csl-quote-attribution">
        — <strong>Gen Xu</strong> · Tech Lead, Infrastructure &amp; Data, Nimble Robotics
      </div>
      <div className="csl-callouts">
        {NIMBLE_STATS.map((s) => (
          <div key={s.head} className="csl-callout">
            <span className="csl-callout-head">{s.head}</span>
            <span className="csl-callout-body">{s.body}</span>
          </div>
        ))}
      </div>
    </div>
  </div>
);

/* ══════════════════════════════════════════════════════════════════════
   Slide 11 — Lakestream Platform · 2 cards (Pulsar + Kafka) matching
   /data-streaming/overview DataStreamingInPracticeSection.vue layout.
   ══════════════════════════════════════════════════════════════════════ */

type PracticeCard = {
  hero: string;
  eyebrow: string;
  title: string;
  desc: string;
  bullets: readonly string[];
  tone: 'teal' | 'blue';
};

const PRACTICE_CARDS: ReadonlyArray<PracticeCard> = [
  {
    hero: inPracticePulsar,
    eyebrow: 'Pulsar Service',
    title: 'Production-Proven Message Queue',
    desc: 'Multi-tenant, durable, geo-replicated messaging with low latency at global scale — for mission-critical businesses such as payments, orders, IoT, and internal services.',
    bullets: [
      'Pub/sub and queue semantics in one system',
      'Strong durability, per-key & per-partition ordering, DLQs & retries',
      'Geo-replication and seamless scaling/maintenance',
      '100× lower tail latency with predictable performance',
    ],
    tone: 'teal',
  },
  {
    hero: inPracticeKafka,
    eyebrow: 'Kafka Service',
    title: 'Lakehouse-Native Kafka Streaming',
    desc: 'Keep your Kafka APIs while Ursa, the storage engine for the runtime, provides a cloud-native, lakehouse-ready engine that eliminates Kafka cluster sprawl and can cut TCO by up to 95% — with no app changes.',
    bullets: [
      'Native Kafka service — tools and apps work unchanged',
      'Writes streams directly to catalog-governed Iceberg/Delta tables',
      'No broker disks or cross-AZ replication; separate compute & storage',
      'Up to 95% lower TCO with predictable performance',
    ],
    tone: 'blue',
  },
];

const PlatformSlide = () => (
  <div className="slide-body">
    <Header
      eyebrow="The Lakestream Platform · Kafka and Pulsar"
      title={<>Two services. <em>One engine.</em> Powered by Ursa.</>}
      sub="Run mission-critical Pulsar messaging and cost-efficient Kafka streaming — all powered by Ursa. No app changes."
    />
    <div className="sn-work">
      <div className="csl-platform-v2-grid">
        {PRACTICE_CARDS.map((c) => (
          <article key={c.eyebrow} className={`csl-platform-v2-card csl-platform-v2-card--${c.tone}`}>
            <div className="csl-platform-v2-hero">
              <img src={c.hero} alt={`${c.eyebrow} hero illustration`} />
            </div>
            <div className="csl-platform-v2-body">
              <span className="csl-platform-v2-eyebrow">{c.eyebrow}</span>
              <h3 className="csl-platform-v2-title">{c.title}</h3>
              <p className="csl-platform-v2-desc">{c.desc}</p>
              <ul className="csl-platform-v2-bullets">
                {c.bullets.map((b) => <li key={b}>{b}</li>)}
              </ul>
            </div>
          </article>
        ))}
      </div>
    </div>
  </div>
);

/* ══════════════════════════════════════════════════════════════════════
   Slide 12 — Pulsar capabilities (3 stacked benefits + figure)
   ══════════════════════════════════════════════════════════════════════ */

type Benefit = { num: string; title: string; body: string };
const PULSAR_BENEFITS: ReadonlyArray<Benefit> = [
  {
    num: '01',
    title: 'Multi-Tenant · Geo-Replicated',
    body: 'Tenant isolation, geo-replication, seamless scale + maintenance — built in.',
  },
  {
    num: '02',
    title: 'Pub/Sub + Queue, Unified',
    body: 'One system. Both messaging patterns. Per-key & per-partition ordering, DLQs, retries.',
  },
  {
    num: '03',
    title: '100× Lower Tail Latency',
    body: 'Predictable performance at global scale — for the workloads that can’t afford to wait.',
  },
];

const PulsarSlide = () => (
  <div className="slide-body">
    <Header
      eyebrowTone="teal"
      eyebrow="Pulsar Service · Production-Proven Messaging"
      titleAccent="teal"
      title={<>The most <em>proven message queue.</em> Now lakehouse-native.</>}
    />
    <div className="sn-work sn-work--row">
      <div className="csl-pulsar-benefits">
        {PULSAR_BENEFITS.map((b) => (
          <article key={b.num} className="csl-benefit-block">
            <span className="csl-benefit-num">{b.num}</span>
            <h3 className="csl-benefit-title">{b.title}</h3>
            <p className="csl-benefit-body">{b.body}</p>
          </article>
        ))}
      </div>
      <div className="csl-pulsar-figure">
        <img src={platformPulsar} alt="Pulsar Service architecture" />
      </div>
    </div>
  </div>
);

/* ══════════════════════════════════════════════════════════════════════
   Slide 13 — Industries (quote on top, 4 industry cards that fill row)
   ══════════════════════════════════════════════════════════════════════ */

type Industry = { name: string; tag: string; img: string };
const INDUSTRIES: ReadonlyArray<Industry> = [
  { name: 'Financial Services', tag: 'Payments · Trading · Fraud',  img: industryFinserv },
  { name: 'MarTech',            tag: 'Engagement · Personalization', img: industryMartech },
  { name: 'Manufacturing',      tag: 'IoT · Telemetry · OEE',        img: industryManufacturing },
  { name: 'Gaming',             tag: 'Real-time leaderboards · Chat', img: industryGaming },
];

const UseCaseSlide = () => (
  <div className="slide-body">
    <Header eyebrowTone="teal" eyebrow="Pulsar · In Production" />
    <div className="sn-work csl-industries-body">
      <blockquote className="csl-quote-hero csl-quote-hero--teal">
        Powering the most <em>user-facing, mission-critical</em> businesses — anywhere downtime isn’t an option.
      </blockquote>
      <div className="csl-industries-grid">
        {INDUSTRIES.map((i) => (
          <article key={i.name} className="csl-industry">
            <img src={i.img} alt={i.name} />
            <div className="csl-industry-meta">
              <h3 className="csl-industry-name">{i.name}</h3>
              <span className="csl-industry-tag">{i.tag}</span>
            </div>
          </article>
        ))}
      </div>
    </div>
  </div>
);

/* ══════════════════════════════════════════════════════════════════════
   Slide 14 — Ecosystem (3 cards w/ website hero images filling card top)
   ══════════════════════════════════════════════════════════════════════ */

type EcoCard = { eyebrow: string; title: string; desc: string; bullets: readonly string[]; img: string; bg: string };
const ECOSYSTEM: ReadonlyArray<EcoCard> = [
  {
    eyebrow: 'Universal Connect',
    title: 'Kafka Connect + Pulsar IO, unified.',
    desc: 'Configure sources and sinks once across DBs, SaaS, warehouses, lakehouses.',
    bullets: [
      '200+ pre-built connectors',
      'Schema-aware transformation',
      'Single config plane',
    ],
    img: ecoConnect,
    bg: ecoConnectBg,
  },
  {
    eyebrow: 'Universal Linking',
    title: 'Mirror Kafka → Ursa. Streams become tables.',
    desc: 'Full-fidelity topics, schemas, offsets. Migrate, fan-out, or DR.',
    bullets: [
      'Topic-fidelity mirroring',
      'Iceberg / Delta auto-emission',
      'Zero-rewrite migration path',
    ],
    img: ecoUniversalLinking,
    bg: ecoUniversalLinkingBg,
  },
  {
    eyebrow: 'Managed Flink · Powered by Ververica',
    title: 'End-to-end stream processing.',
    desc: 'Built by the team behind Apache Flink. VERA engine — 2× throughput.',
    bullets: [
      '2× throughput vs OSS Flink',
      'Full Flink API compatibility',
      'SQL + DataStream + Table APIs',
    ],
    img: ecoFlink,
    bg: ecoFlinkBg,
  },
];

const EcosystemSlide = () => (
  <div className="slide-body">
    <Header eyebrow="Ecosystem · One Platform" title={<>Connect. Link. <em>Process.</em></>} />
    <div className="sn-work">
      <div className="csl-eco-grid">
        {ECOSYSTEM.map((c) => (
          <article key={c.eyebrow} className="csl-eco-card">
            <div className="csl-eco-card-hero">
              <img className="csl-eco-card-bg" src={c.bg} alt="" aria-hidden />
              <div className="csl-eco-card-glass" aria-hidden />
              <img className="csl-eco-card-visual" src={c.img} alt="" />
            </div>
            <span className="csl-eco-card-eyebrow">{c.eyebrow}</span>
            <h3 className="csl-eco-card-title">{c.title}</h3>
            <p className="csl-eco-card-desc">{c.desc}</p>
            <ul className="csl-eco-card-bullets">
              {c.bullets.map((b) => <li key={b}>{b}</li>)}
            </ul>
          </article>
        ))}
      </div>
    </div>
  </div>
);

/* ══════════════════════════════════════════════════════════════════════
   Slide 15 — Deploy · 4 tiers grouped by 3 categorical pills
   Copy verbatim from streamnative.io/pricing (Serverless / Dedicated /
   BYOC) and streamnative.io/cloud/private-cloud (Private Cloud).
   ══════════════════════════════════════════════════════════════════════ */

type DeployTier = {
  tag: string;
  name: string;
  price: string;
  desc: string;
  bullets: readonly string[];
  highlighted?: boolean;
};
const DEPLOY_TIERS: ReadonlyArray<DeployTier> = [
  {
    tag: 'Free trial',
    name: 'Serverless',
    price: 'Pay per use',
    desc: 'The fastest way to start. Hosted on shared infrastructure, fully managed.',
    bullets: [
      'Instant clusters · zero base cost',
      'Autoscale to workload with predictable performance',
      'All protocols (Pulsar, Kafka, MQTT, REST)',
    ],
  },
  {
    tag: 'Pro available',
    name: 'Dedicated',
    price: 'Your own cluster',
    desc: 'Dedicated clusters hosted in AWS, GCP, or Azure — fully managed.',
    bullets: [
      'Enterprise SLAs · single / multi-AZ',
      'Dedicated resources · stronger isolation',
      'All protocols · built-in geo-replication',
    ],
    highlighted: true,
  },
  {
    tag: 'Pro available',
    name: 'BYOC',
    price: 'Your VPC',
    desc: 'Bring Your Own Cloud. Clusters in your cloud account, managed by us.',
    bullets: [
      'Data stays in your infrastructure',
      'Dedicated VPC · Private Link',
      'Any region across AWS, GCP, or Azure',
    ],
  },
  {
    tag: 'Self-managed',
    name: 'Private Cloud',
    price: 'Your infrastructure',
    desc: 'Self-managed StreamNative software. Native Pulsar & Kafka on your own clusters.',
    bullets: [
      'Enterprise-grade uptime SLA',
      'Cloud-native · Kubernetes operators',
      'Hybrid & multi-cloud ready',
    ],
  },
];

type DeployPill = { label: string; span: number };
const DEPLOY_PILLS: ReadonlyArray<DeployPill> = [
  { label: 'Managed Backups & Upgrades · 24/7 Monitoring', span: 2 },
  { label: 'Deploy in your cloud', span: 1 },
  { label: 'Self-managed software', span: 1 },
];

const DeploySlide = () => (
  <div className="slide-body">
    <Header
      eyebrow="Deploy · Your Way"
      title={<>Serverless · Dedicated · BYOC · <em>Private Cloud.</em></>}
    />
    <div className="sn-work">
      <div className="csl-deploy-pills-v2">
        {DEPLOY_PILLS.map((p) => (
          <span
            key={p.label}
            className="csl-deploy-pill"
            style={{ gridColumn: `span ${p.span}` }}
          >
            {p.label}
          </span>
        ))}
      </div>
      <div className="csl-deploy-grid">
        {DEPLOY_TIERS.map((t) => (
          <article
            key={t.name}
            className={`csl-deploy-card${t.highlighted ? ' csl-deploy-card--highlighted' : ''}`}
          >
            <span className="csl-deploy-tag">{t.tag}</span>
            <h3 className="csl-deploy-name">{t.name}</h3>
            <span className="csl-deploy-price">{t.price}</span>
            <p className="csl-deploy-desc">{t.desc}</p>
            <ul className="csl-deploy-bullets">
              {t.bullets.map((b) => <li key={b}>{b}</li>)}
            </ul>
          </article>
        ))}
      </div>
    </div>
  </div>
);

/* ══════════════════════════════════════════════════════════════════════
   Slide 16 — Customer logo wall · 14 brands (Contextual dropped; Q6/TEG/
   Nimble use real logos at larger size)
   ══════════════════════════════════════════════════════════════════════ */

type Brand = { src: string; alt: string };
const BRANDS: ReadonlyArray<Brand> = [
  { src: logoInnerspace,      alt: 'Innerspace' },
  { src: logoSafariai,        alt: 'Safari AI' },
  { src: logoUnify,           alt: 'Unify' },
  { src: logoIntercom,        alt: 'Intercom' },
  { src: logoIterable,        alt: 'Iterable' },
  { src: logoCisco,           alt: 'Cisco' },
  { src: logoToyota,          alt: 'Toyota' },
  { src: logoAppliedMaterials,alt: 'Applied Materials' },
  { src: logoNarvar,          alt: 'Narvar' },
  { src: logoNimble,          alt: 'Nimble Robotics' },
  { src: logoNetdata,         alt: 'Netdata' },
  { src: logoIntuit,          alt: 'Intuit' },
];

const LogosSlide = () => (
  <div className="slide-body">
    <Header
      eyebrow="Customers"
      title={<>Trusted for <em>mission-critical data and AI.</em></>}
    />
    <div className="sn-work">
      <div className="csl-logo-wall">
        {BRANDS.map((b) => (
          <span key={b.alt} className="csl-logo-tile">
            <img className="sn-logo" src={b.src} alt={b.alt} />
          </span>
        ))}
      </div>
    </div>
  </div>
);

/* ══════════════════════════════════════════════════════════════════════
   Slide order — speaker notes preserved
   ══════════════════════════════════════════════════════════════════════ */

type SlideEntry = {
  section: string;
  notes: readonly string[];
  render: () => ReactNode;
};

/** Build the 17-slide booth deck, baking per-event data into slides 1 + 17. */
export function buildBoothSlides(config: BoothDeckConfig): readonly Slide[] {
  const { titleEyebrow, promo } = config;

  const TitleSlideEl = () => (
    <TitleSlide
      eyebrow={titleEyebrow}
      title={<>Escape the <em>Traps</em> of Traditional Kafka.</>}
      sub={
        <>
          Native Apache Kafka on Lakestream. <strong>Leaderless. Diskless. Stateless.</strong>{' '}
          Up to <strong>95% lower TCO</strong> — without changing a line of client code.
        </>
      }
    />
  );

  const CtaSlideEl = () => (
    <div className="slide-body">
      <Header
        eyebrow={promo.eyebrow}
        title={<>Get <em>{promo.amount}</em> in cloud credits.</>}
        sub={
          <>
            Bring a workload. We&apos;ll help you size it, port it, and prove the savings.
          </>
        }
      />
      <div className="sn-work sn-work--row">
        <div className="csl-cta-text">
          <p className="csl-cta-promo">
            Use code <code className="sn-cta-code">{promo.code}</code> at signup.{' '}
            <strong>Offer valid {promo.validity}.</strong>
          </p>
          <ul className="csl-feature-list">
            <li>Native Apache Kafka 4.2 powered by Ursa — zero client changes</li>
            <li>Every Kafka topic is also an Iceberg/Delta table</li>
            <li>Up to 95% lower TCO at 5 GB/s (VLDB 2025 best paper)</li>
            <li>Pulsar Service for mission-critical messaging</li>
            <li>Serverless · Dedicated · BYOC · Private Cloud deployment</li>
          </ul>
          <p className="csl-cta-url">{promo.signupDisplayUrl}</p>
        </div>
        <div className="csl-cta-right">
          <div className="csl-qr-transparent">
            <QRCodeSVG value={promo.signupUrl} size={280} level="M" bgColor="transparent" fgColor="#202939" marginSize={0} />
          </div>
          <span className="sn-mono">Scan to sign up</span>
        </div>
      </div>
    </div>
  );

  const ENTRIES: ReadonlyArray<SlideEntry> = [
  {
    section: 'Welcome',
    notes: ['Open question: how much are you spending on Kafka today? Most of it isn’t compute — it’s replication, storage, and inter-AZ traffic. We built Ursa to eliminate those line items.'],
    render: TitleSlideEl,
  },
  {
    section: 'Traditional Kafka · Cost Drivers',
    notes: ['Walk the cost drivers in the diagram: inter-zone transfer, disk-based replication, over-provisioned compute, connector overhead, duplicated copies. Ask which one hurts them the most.'],
    render: TrapsSlide,
  },
  {
    section: 'StreamNative Ursa · Cost Drivers Eliminated',
    notes: ['Each driver on slide 2 disappears here. Same Kafka API, same clients, lakehouse-native engine. The cost driver disappears at the architectural layer.'],
    render: UrsaSlide,
  },
  {
    section: 'UFK Launch · April 2026',
    notes: ['UFK is a native Apache Kafka 4.2+ fork — not a compat layer. Existing producers and consumers work unchanged. UFK extends Kafka local disk storage with Lakestream’s lakehouse-native storage foundation.'],
    render: LaunchSlide,
  },
  {
    section: 'Stream ↔ Table · Inside UFK',
    notes: ['One copy of data, two interfaces. Streaming ships Kafka and the same bytes are queryable as an Iceberg or Delta table — no separate ETL, no duplicate storage.'],
    render: DualitySlide,
  },
  {
    section: 'The Lakestream Architecture',
    notes: ['One unified architecture; three independent layers: Protocol (stateless multi-protocol serving), Metadata (Lakestream Catalog), Data (Ursa — WAL + Parquet on object storage). Each scales on its own; all share the same governed data.'],
    render: LakestreamArchitectureSlide,
  },
  {
    section: 'Proof · Ursa won the best industry paper of VLDB 2025',
    notes: ['Three benefits, not implementation details: validated at scale (5 GB/s, RF=3, 3 AZs); up to 95% lower TCO; zero client changes. QR opens the full PVLDB paper.'],
    render: AwardSlide,
  },
  {
    section: 'Proof · 5 GB/s at ~5% the Cost of Kafka',
    notes: ['5 GB/s sustained, RF=3 across 3 AZs. ~$54/hour Ursa vs ~$303/hour MSK vs ~$988/hour Redpanda. The savings are inter-AZ networking and storage — the structural costs Ursa eliminates.'],
    render: BenchmarkSlide,
  },
  {
    section: 'Proof · Databricks Endorses the Approach',
    notes: ['Karthik Ramasamy ran StreamNative for years and now leads streaming at Databricks. Independent validation of the Lakehouse-first streaming approach.'],
    render: DatabricksQuoteSlide,
  },
  {
    section: 'Customer · Nimble Robotics',
    notes: ['Robotics 3PL — fleet of robots emits IoT-shaped sensor data 24/7. Migrated from Confluent Cloud. 4–5× cost reduction unlocked fleet-wide telemetry and AI training pipelines that weren’t financially viable before.'],
    render: CaseSlide,
  },
  {
    section: 'The Lakestream Platform',
    notes: ['Two services, one engine. Pulsar Service for production-proven messaging. Kafka Service for lakehouse-native streaming powered by Ursa — zero app changes, up to 95% lower TCO.'],
    render: PlatformSlide,
  },
  {
    section: 'Pulsar Service · Capabilities',
    notes: ['The most proven message queue, now lakehouse-native. Multi-tenant, durable, geo-replicated. 100× lower tail latency.'],
    render: PulsarSlide,
  },
  {
    section: 'Pulsar · In Production',
    notes: ['Financial services, MarTech, manufacturing, gaming — anywhere downtime isn’t an option. Narvar runs billions of post-purchase interactions on Pulsar globally.'],
    render: UseCaseSlide,
  },
  {
    section: 'Ecosystem · Connect / Link / Process',
    notes: ['Universal Connect unifies Kafka Connect and Pulsar IO. Universal Linking mirrors existing Kafka clusters into Ursa with full fidelity. Managed Flink (Ververica VERA) handles stream processing.'],
    render: EcosystemSlide,
  },
  {
    section: 'Deploy · Your Way',
    notes: ['Four deployment shapes: Serverless and Dedicated are fully managed (with managed backups, upgrades, 24/7 monitoring); BYOC runs in your cloud; Private Cloud is self-managed software on your own infrastructure.'],
    render: DeploySlide,
  },
  {
    section: 'Trusted for Mission-Critical Data and AI',
    notes: ['Customers across AI, IoT, MarTech, e-commerce, financial services — Cisco, Toyota, Intuit, Narvar, and many more. Ask about an industry they recognize.'],
    render: LogosSlide,
  },
  {
    section: 'Try It · $1,000 Cloud Credits',
    notes: [`Promo code ${promo.code} is good for ${promo.amount} of credits, valid ${promo.validity}. Scan the QR or type the URL. Bring us a workload — we’ll size it, port it, and prove the savings.`],
    render: CtaSlideEl,
  },
];

  return ENTRIES.map((e, i) => ({
    id: i + 1,
    section: e.section,
    theme: 'beige',
    notes: e.notes,
    render: e.render,
  })) as readonly Slide[];
}

/** Assemble a full booth Deck from a per-conference config. */
export function buildBoothDeck(config: BoothDeckConfig): Deck {
  return {
    slug: config.slug,
    category: 'booth-decks',
    title: config.title ?? DEFAULT_BOOTH_TITLE,
    speaker: 'StreamNative',
    description: config.description,
    date: config.eventDate,
    context: config.context,
    visibility: 'public',
    autoplayMs: config.autoplayMs ?? 10_000,
    slides: buildBoothSlides(config),
  };
}
